// @flow
import React from 'react';
import { FormattedMessage } from 'react-intl';
import Box from "@material-ui/core/Box";
import makeStyles from "@material-ui/core/styles/makeStyles";
import type { Config as ConfigType } from '../../../types';
import satelliteIconSrc from '../../../assets/images/satellite.svg';
import Dot from '../../ui/Dot';
import Section from './Section';
import ConfigItem from './ConfigItem';

const useStyles = makeStyles(theme => ({
    durations: {
        '& .MuiList-root': {
            padding: 0
        },
        '& .MuiListItem-container': {
            display: 'flex',
            alignItems: 'center',
            marginBottom: theme.spacing(1.5),
            '&:last-child': {
                marginBottom: 0
            }
        },
        '& .MuiListItem-root': {
            paddingTop: 0,
            paddingLeft: 0,
            paddingBottom: 0,
            // marginTop: theme.spacing(.5),
            // marginBottom: theme.spacing(1)
        },
        '& .MuiFormControl-root': {
            width: '100%'
        },
        '& .MuiInputBase-input': {
            textAlign: 'right'
        }
    }
}));

const parseIntArray = (values: $ReadOnlyArray<string>) => values.map(value => parseInt(value, 10));

export type Props = $ReadOnly<{
    config: ConfigType
}>;
function Config({ config }: Props) {
    const classes = useStyles();

    return (
        <Box m={2}>
            <Section title={(
                <FormattedMessage
                    id="option.config.section.general"
                    defaultMessage="General"
                />
            )}>
                <ConfigItem
                    type="boolean"
                    label={(
                        <FormattedMessage
                            id="options.config.general.showLabels"
                            defaultMessage="Show target labels"
                        />
                    )}
                    path={['general', 'showLabels']}
                />
                <ConfigItem
                    type="boolean"
                    label={(
                        <FormattedMessage
                            id="options.config.general.clusterLabels"
                            defaultMessage="Cluster target labels"
                        />
                    )}
                    path={['general', 'clusterLabels']}
                />
                <ConfigItem
                    type="boolean"
                    label={(
                        <FormattedMessage
                            id="options.config.general.showTrails"
                            defaultMessage="Show target trails"
                        />
                    )}
                    path={['general', 'showTrails']}
                />
            </Section>
            <Section title={(
                <FormattedMessage
                    id="option.config.section.session"
                    defaultMessage="Session"
                />
            )}>
                <ConfigItem
                    type="number"
                    label={(
                        <FormattedMessage
                            id="options.config.session.active"
                            defaultMessage="Active"
                        />
                    )}
                    path={['session', 'active']}
                    prepend={<Dot color="green" />}
                    append="ms"
                />
                <ConfigItem
                    type="number"
                    label={(
                        <FormattedMessage
                            id="options.config.session.medium"
                            defaultMessage="Medium"
                        />
                    )}
                    path={['session', 'medium']}
                    prepend={<Dot color="yellow" />}
                    append="ms"
                />
                <ConfigItem
                    type="number"
                    label={(
                        <FormattedMessage
                            id="options.config.session.low"
                            defaultMessage="Low"
                        />
                    )}
                    path={['session', 'low']}
                    prepend={<Dot color="red" />}
                    append="ms"
                />
                <ConfigItem
                    type="number"
                    label={(
                        <FormattedMessage
                            id="options.config.session.disconnected"
                            defaultMessage="Disconnected"
                        />
                    )}
                    path={['session', 'disconnected']}
                    prepend={<Dot color="gray" />}
                    append="ms"
                />
                <ConfigItem
                    type="number"
                    label={(
                        <FormattedMessage
                            id="options.config.session.fallbackTimeout"
                            defaultMessage="Use device data source after"
                        />
                    )}
                    path={['session', 'fallbackTimeout']}
                    prepend={<img src={satelliteIconSrc} />}
                    append="ms"
                />
            </Section>

            <Section title={(
                <FormattedMessage
                    id="option.config.section.runways"
                    defaultMessage="Runways"
                />
            )}>
                <ConfigItem
                    type="array"
                    label={(
                        <FormattedMessage
                            id="options.config.runways.arrival"
                            defaultMessage="Arrival"
                        />
                    )}
                    path={['runways', 'arrival']}
                    sortable
                />
                <ConfigItem
                    type="array"
                    label={(
                        <FormattedMessage
                            id="options.config.runways.departure"
                            defaultMessage="Departure"
                        />
                    )}
                    path={['runways', 'departure']}
                    sortable
                />
            </Section>

            <Section title={(
                <FormattedMessage
                    id="option.config.section.zones"
                    defaultMessage="Zones"
                />
            )}>
                <ConfigItem
                    type="array"
                    label={(
                        <FormattedMessage
                            id="options.config.zones.durations"
                            defaultMessage="Durations"
                        />
                    )}
                    path={['zones', 'durations']}
                    minItems={1}
                    maxItems={4}
                    editable
                    expanded
                    canClose={false}
                    className={ classes.durations }
                    outputFormatter={parseIntArray}
                    append="min"
                />
            </Section>

            <Section title={(
                <FormattedMessage
                    id="option.config.section.debug"
                    defaultMessage="Debug"
                />
            )}>
                <ConfigItem
                    type="boolean"
                    label={(
                        <FormattedMessage
                            id="options.config.debug.bothSource"
                            defaultMessage="Show both data sources on map"
                        />
                    )}
                    path={['debug', 'showBothSources']}
                />
                <ConfigItem
                    type="boolean"
                    label={(
                        <FormattedMessage
                            id="options.config.debug.keepDisconnected"
                            defaultMessage="Keep disconnected targets on map"
                        />
                    )}
                    path={['debug', 'showDisconnected']}
                />
                <ConfigItem
                    type="boolean"
                    label={(
                        <FormattedMessage
                            id="options.config.debug.simulateLossOfSystemData"
                            defaultMessage="Simulate loss of system data source"
                        />
                    )}
                    path={['debug', 'discardSystemDataSource']}
                />
                <ConfigItem
                    type="boolean"
                    label={(
                        <FormattedMessage
                            id="options.config.debug.simulateLossOfHeadingData"
                            defaultMessage="Simulate loss of heading data"
                        />
                    )}
                    path={['debug', 'discardHeadingData']}
                />
                <ConfigItem
                    type="boolean"
                    label={(
                        <FormattedMessage
                            id="options.config.debug.simulateLossOfSpeedData"
                            defaultMessage="Simulate loss of speed data"
                        />
                    )}
                    path={['debug', 'discardSpeedData']}
                />
                <ConfigItem
                    type="boolean"
                    label={(
                        <FormattedMessage
                            id="options.config.debug.tisPlayer"
                            defaultMessage="Enable TIS Player"
                        />
                    )}
                    path={['debug', 'tisPlayer']}
                />

                <ConfigItem
                    type="text"
                    label={(
                        <FormattedMessage
                            id="options.config.debug.tisFilter"
                            defaultMessage="TIS Filter"
                        />
                    )}
                    path={['debug', 'tisFilter']}
                />

                <ConfigItem
                    type="boolean"
                    label={(
                        <FormattedMessage
                            id="options.config.debug.tisLogger"
                            defaultMessage="Enable TIS Logger"
                        />
                    )}
                    path={['debug', 'tisLogger']}
                />
                <ConfigItem
                    type="text"
                    label={(
                        <FormattedMessage
                            id="options.config.debug.tisLoggerFilter"
                            defaultMessage="TIS Logger Filter"
                        />
                    )}
                    path={['debug', 'tisLoggerFilter']}
                    disabled={!config.debug || !config.debug.tisLogger}
                />
            </Section>
        </Box>
    );
}

export default Config;